import { DEFAULT_WEEKLY } from '@/constants/availability';
import useAuth from '@/hooks/useAuth';
import { useEditEventTypeMutation } from '@/queries/eventTypes.queries';
import { useCreateResourceMutation, useListResourcesQuery } from '@/queries/resources.queries';
import IEventType from '@/types/IEventType';
import { IEventTypeWrite } from '@/types/apiClient/IEventTypeWrite';
import { useDisclosure } from '@chakra-ui/react';
import { useIsFetching, useIsMutating } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

interface IArgs {
	eventType: IEventType;
	onChange: (values: Partial<IEventTypeWrite>) => void;
	onEditAvailabilityClick: (id: string) => void;
}

export function useRequiredParticipants(args: IArgs) {
	const isMutation = useIsMutating() > 0;
	const isFetching = useIsFetching() > 0;
	const { t } = useTranslation();
	const disclosure = useDisclosure();
	const availabilitiesQuery = useListResourcesQuery();
	const createResourceMutation = useCreateResourceMutation();
	const auth = useAuth();
	const connectedCalendars = auth.getCurrentUser().thirdPartyCalendars;
	const updateEventTypeMutation = useEditEventTypeMutation();

	function createAvailability() {
		const primaryCalendars = connectedCalendars.filter((c) => c.primary);
		/**
		 * We use the user ID as ID for the default availability
		 */
		const id = auth.getCurrentUser().userId;
		return createResourceMutation
			.mutateAsync({
				id,
				name: auth.getCurrentUser().slug,
				bookingRules: {
					/**
					 * Since this resource represents a user
					 * we only allow one booking at a time.
					 */
					maxConcurrentBookings: 1,
					repeatingAvailability: {
						timeZone: auth.getCurrentUser().timeZone,
						weekly: DEFAULT_WEEKLY,
					},
					maxBookingNotice: 'PT720H',
					minBookingNotice: 'PT6H',
					checkAvailabilityInCalendars: primaryCalendars,
				},

				tags: {
					tt_default: 'true',
				},
				collaborators: [],
			})
			.then(() => {
				return updateEventTypeMutation.mutateAsync({
					...args.eventType,
					resourceRules: {
						...(args.eventType.resourceRules ?? {}),
						linkedResources: [...(args.eventType.resourceRules?.linkedResources || []), { id }],
					},
				});
			})
			.then(() => {
				args.onEditAvailabilityClick(id);
			})
			.catch(() => {});
	}

	function handleAddRequiredParticipantClick() {
		if (!availabilitiesQuery.isFetched) {
			return;
		}

		if (!availabilitiesQuery.data?.resources.length) {
			return createAvailability();
		}

		disclosure.onOpen();
	}

	return {
		handleAddRequiredParticipantClick,
		disclosure,
		t,
		isLoading: isFetching || isMutation,
	};
}
